import { useEffect, useRef, useState } from 'react';
import { chapters } from '../data';
import { useReducedMotion } from '../motion';
import { closestStoryId, type StoryId } from '../story';
import { PhoneStage } from './PhoneStage';

export function ProductStory() {
  const [active, setActive] = useState<StoryId>('feed');
  const activeRef = useRef<StoryId>('feed');
  const reducedMotion = useReducedMotion();

  useEffect(() => {
    const ratios = new Map<string, number>();
    const nodes = chapters
      .map(({ id }) => document.getElementById(`story-${id}`))
      .filter((node): node is HTMLElement => Boolean(node));

    if (!('IntersectionObserver' in window)) return;

    const observer = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) ratios.set(entry.target.id.replace('story-', ''), entry.intersectionRatio);
        const next = closestStoryId(
          Array.from(ratios, ([id, ratio]) => ({ id, ratio })),
          activeRef.current,
        );
        if (next !== activeRef.current) {
          activeRef.current = next;
          setActive(next);
        }
      },
      { rootMargin: '-25% 0px -35%', threshold: [0, 0.2, 0.4, 0.6, 0.8, 1] },
    );

    nodes.forEach((node) => observer.observe(node));
    return () => observer.disconnect();
  }, []);

  return (
    <section className="product-story" id="product" aria-labelledby="product-title">
      <div className="section-kicker layout-bound">
        <span>THE PRODUCT STORY</span>
        <span>SCROLL NATURALLY · NO SCROLL JACKING</span>
      </div>
      <div className="story-heading layout-bound">
        <h2 id="product-title">One night.<br />A life around it.</h2>
        <p>Five views of the same idea: concerts should become more valuable after the lights come up.</p>
      </div>
      <div className="story-layout layout-bound">
        <div className="story-stage-desktop">
          <PhoneStage active={active} />
          <nav className="scene-dots" aria-label="Product preview scenes">
            {chapters.map((chapter) => (
              <button
                key={chapter.id}
                type="button"
                className={active === chapter.id ? 'is-active' : ''}
                aria-label={`Show ${chapter.id} preview`}
                aria-pressed={active === chapter.id}
                onClick={() => {
                  document.getElementById(`story-${chapter.id}`)?.scrollIntoView({
                    behavior: reducedMotion ? 'auto' : 'smooth',
                    block: 'center',
                  });
                }}
              />
            ))}
          </nav>
        </div>
        <div className="story-copy">
          {chapters.map((chapter) => (
            <article
              className={`story-chapter ${active === chapter.id ? 'is-active' : ''}`}
              id={`story-${chapter.id}`}
              key={chapter.id}
              data-story-id={chapter.id}
            >
              <div className="chapter-rule"><span>{chapter.number}</span><span>{chapter.id.toUpperCase()}</span></div>
              <h3>{chapter.title}</h3>
              <p>{chapter.body}</p>
              <ul>
                {chapter.facts.map((fact) => <li key={fact}>{fact}</li>)}
              </ul>
              <div className="story-stage-mobile">
                <PhoneStage active={chapter.id} compact />
              </div>
            </article>
          ))}
        </div>
      </div>
    </section>
  );
}
